import { html } from "htm/preact";
import {
  canInstall,
  dismissInstallPrompt,
  isIOS,
  promptInstall,
  shouldShowInstallHint,
  type HintScope,
} from "../installPrompt.js";
import { TERMS } from "../copy.js";

/** Prompted during first-run setup (spec §7): installed PWAs are exempt from
 * Safari's ~week-long eviction of script-writable storage, which matters here since
 * rounds may be days or weeks apart.
 *
 * On the create screen it makes a sharper point. A game is only as reachable as the
 * device that made it: adding the app to your home screen afterwards gives you a second
 * copy with its own storage and none of your games in it, and on a phone a tapped
 * recovery link opens the browser rather than that app. Setting a game up from inside
 * the installed app costs one tap now and saves that whole problem. */
export function InstallHint({ scope = "home" }: { scope?: HintScope } = {}) {
  if (!shouldShowInstallHint(scope)) return null;

  const why =
    scope === "create"
      ? html`
          <p>
            ${`Set this ${TERMS.event} up from your home screen and it stays with you. Add
              the app first — one added afterwards starts empty, and it can't be handed
              anything from your browser.`}
          </p>
        `
      : null;

  return html`
    <div class="card card-accent">
      ${why}
      ${isIOS && !canInstall.value
        ? html`
            <p>
              ${scope === "create"
                ? html`To add it: tap <strong>Share</strong> → <strong>Add to Home Screen</strong>.`
                : html`Add this to your home screen so it works reliably offline between
                    rounds: tap <strong>Share</strong> → <strong>Add to Home Screen</strong>.`}
            </p>
            <button class="secondary" onClick=${() => dismissInstallPrompt(scope)}>
              ${scope === "create" ? "Carry on here" : "Got it"}
            </button>
          `
        : html`
            ${scope === "create"
              ? null
              : html`<p>
                  Install this app to your home screen so it keeps working offline between
                  rounds.
                </p>`}
            <div class="row">
              <button class="grow" onClick=${promptInstall}>Install</button>
              <button class="secondary" onClick=${() => dismissInstallPrompt(scope)}>
                ${scope === "create" ? "Carry on here" : "Not now"}
              </button>
            </div>
          `}
    </div>
  `;
}
